import { useMutation, useQueryClient } from '@tanstack/react-query';
import { memo, useState } from 'react';

import { Button } from '@doger/ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@doger/ui/components/ui/dialog';
import { $$toast } from '@doger/ui/components/ui/toast';

import type { FindOneUserResponse } from '@doger/admin/shared/api';
import { $$privateApi } from '@doger/admin/shared/api';

import styles from './whitelist-toggle.module.scss';

type WhitelistToggleProps = {
  user: FindOneUserResponse;
};

export const WhitelistToggle = memo<WhitelistToggleProps>(({ user }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const toggleWhitelistMutation = useMutation({
    mutationFn: ({
      userId,
      isWhitelisted,
    }: {
      userId: string;
      isWhitelisted: boolean;
    }) => $$privateApi.toggleUserWhitelist({ userId, isWhitelisted }),

    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        ['user', user.id],
        (old: FindOneUserResponse) => {
          if (!old) return old;

          return {
            ...old,
            isWhitelisted: variables.isWhitelisted,
          };
        }
      );

      queryClient.setQueryData(
        ['users'],
        (old: { pageParams: number[]; pages: FindOneUserResponse[] }) => {
          if (!Array.isArray(old?.pages)) return old;

          return {
            ...old,
            pages: old.pages.map((user) =>
              user.id === variables.userId
                ? { ...user, isWhitelisted: variables.isWhitelisted }
                : user
            ),
          };
        }
      );

      queryClient.invalidateQueries({ queryKey: ['user', user.id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });

      setIsOpen(false);

      if (variables.isWhitelisted) {
        $$toast.success(`User ${user.email} has been added to the whitelist.`);
      } else {
        $$toast.success(
          `User ${user.email} has been removed from the whitelist.`
        );
      }
    },
    onError: (error, variables) => {
      console.error(error);

      if (variables.isWhitelisted) {
        $$toast.error(`Failed to add user to whitelist`, {
          description: `An error occurred while adding the user to the whitelist. Please try again.`,
        });
      } else {
        $$toast.error(`Failed to remove user from whitelist`, {
          description: `An error occurred while removing the user from the whitelist. Please try again.`,
        });
      }
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant={user.isWhitelisted ? 'outlined' : 'contained'}
          size='md'
          palette={user.isWhitelisted ? 'ghost' : 'primary'}
          disabled={toggleWhitelistMutation.isPending}
        >
          {user.isWhitelisted ? 'Remove from Whitelist' : 'Add to Whitelist'}
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {!user.isWhitelisted
              ? `Add user to Whitelist`
              : `Remove user from Whitelist`}
          </DialogTitle>
          <DialogDescription>
            {!user.isWhitelisted
              ? 'Are you sure you want to add this user to the whitelist? This will grant them access to AI features.'
              : 'Are you sure you want to remove this user from the whitelist? This will restrict their access to AI features.'}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className={styles.dialogFooter}>
          <Button
            variant='outlined'
            palette='ghost'
            onClick={() => {
              setIsOpen(false);
              $$toast.info('Whitelist change cancelled', {
                description:
                  'No changes were made to the user whitelist status.',
              });
            }}
            disabled={toggleWhitelistMutation.isPending}
            size='lg'
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            palette='primary'
            onClick={() => {
              toggleWhitelistMutation.mutate({
                userId: user.id,
                isWhitelisted: !user.isWhitelisted,
              });
            }}
            disabled={toggleWhitelistMutation.isPending}
            size='lg'
          >
            {toggleWhitelistMutation.isPending
              ? 'Processing...'
              : !user.isWhitelisted
                ? 'Add to Whitelist'
                : 'Remove from Whitelist'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
